import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { black, white } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';
import { TextField, TextFieldProps } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';

import type { UpdateSchoolFormValues } from '@modules/schools/requests';

const StyledTextFieldLabel = styled.span`
    b {
        font-weight: 600;
    }
`;

const Document = styled.div`
    padding-bottom: 20px;

    & > *:not(:last-child) {
        margin-bottom: 16px;
    }

    ${breakpoints.down('xs')} {
        padding-bottom: 0;
    }
`;

const Description = styled.p`
    font-size: 1.4rem;
    line-height: 1.5;
    font-weight: 600;
    margin-bottom: 32px;
`;

const DocumentText = styled.p`
    font-size: 1.4rem;
    line-height: 1.6;
    font-weight: 400;

    ${breakpoints.down('xs')} {
        font-size: 1.2em;
        line-height: 1.4;
    }
`;

const LinearGradientText = styled(DocumentText)<{ direction?: 'bottom' | 'top' }>`
    ${({ direction }) =>
        direction === 'top'
            ? `
          position: relative;
          background: linear-gradient(180deg, ${white[80]}, ${black[100]});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          color: transparent;

          &:before {
            content: '';
            position: absolute;
            z-index: 0;
            top: -16px;
            left: 0;
            width: 100%;
            height: 2px;
            border-top: 2px dashed ${black[10]};
        }
    `
            : `
          background: linear-gradient(0deg, ${white[80]}, ${black[100]});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          color: transparent;
    `}
`;

const DocumentFormGroup = React.memo((): React.ReactElement => {
    const { t } = useTranslation(['common', 'errors']);

    const { values, errors, handleChange, handleBlur } = useFormikContext<UpdateSchoolFormValues>();

    const commonProps = React.useMemo(
        (): Partial<TextFieldProps> => ({
            fullWidth: true,
            required: true,
            onChange: handleChange,
            onBlur: handleBlur,
            inputProps: {
                // Вырубаем дефолтную валидацию input required, в самом TextField оставляем, т.к. нужна
                // красная звездочка в ui (валидация будет через formik)
                required: false,
            },
        }),
        [handleChange, handleBlur],
    );

    return (
        <FormStyled.FormRow>
            <FormStyled.FormColumnTitle>
                {t('common:form.labels.requisites.titles.client')}
            </FormStyled.FormColumnTitle>

            <Description>{t('common:blocks.contract.description')}</Description>

            <Document>
                <LinearGradientText direction='top'>
                    {t('common:blocks.contract.start')}
                </LinearGradientText>

                <TextField
                    {...commonProps}
                    id='fullName'
                    label={t('common:form.labels.requisites.school_long_name')}
                    placeholder={t('common:form.placeholder.requisites.school_long_name') ?? ''}
                    value={values.fullName}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                />

                <DocumentText>{t('common:blocks.contract.signer')}</DocumentText>

                <TextField
                    {...commonProps}
                    id='requisites.signerPositionGenitive'
                    label={
                        <StyledTextFieldLabel>
                            <Trans
                                t={t}
                                i18nKey='common:form.labels.requisites.signer_position_genitive'
                                components={{
                                    b: <b />,
                                }}
                            />
                        </StyledTextFieldLabel>
                    }
                    placeholder={
                        t('common:form.placeholder.requisites.signer_position_genitive') ?? ''
                    }
                    value={values.requisites?.signerPositionGenitive}
                    error={!!errors.requisites?.signerPositionGenitive}
                    helperText={errors.requisites?.signerPositionGenitive}
                />

                <TextField
                    {...commonProps}
                    id='requisites.signerNameGenitive'
                    label={
                        <StyledTextFieldLabel>
                            <Trans
                                t={t}
                                i18nKey='common:form.labels.requisites.signer_name_genitive'
                                components={{
                                    b: <b />,
                                }}
                            />
                        </StyledTextFieldLabel>
                    }
                    placeholder={t('common:form.placeholder.requisites.signer_name_genitive') ?? ''}
                    value={values.requisites?.signerNameGenitive}
                    error={!!errors.requisites?.signerNameGenitive}
                    helperText={errors.requisites?.signerNameGenitive}
                />

                <DocumentText>{t('common:blocks.contract.license')}</DocumentText>

                <TextField
                    {...commonProps}
                    id='requisites.licence'
                    label={
                        <Trans
                            t={t}
                            i18nKey='common:form.labels.requisites.license'
                            components={{
                                b: <b />,
                            }}
                        />
                    }
                    placeholder={t('common:form.placeholder.requisites.license') ?? ''}
                    value={values.requisites?.licence}
                    error={!!errors.requisites?.licence}
                    helperText={errors.requisites?.licence}
                />

                <LinearGradientText direction='bottom'>
                    {t('common:blocks.contract.end')}
                </LinearGradientText>
            </Document>
        </FormStyled.FormRow>
    );
});

export { DocumentFormGroup };
